import { useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";

import { useTenantScope } from "../../hooks/useTenantScope";
import { useGetEventTypesQuery } from "../../store/services/events-types";
import {
  useDeleteShareSettingMutation,
  useGetShareSettingsQuery,
  useUpsertShareSettingMutation,
} from "../../store/services/tenantShareSettings";
import {
  SHARE_TEXT_MAX_LENGTH,
  TenantShareSetting,
  formatShareSettingScopeLabel,
  isSameShareSettingScope,
} from "../../models/tenantShareSettings";
import { getApiErrorMessage } from "../../utils/functions/api-errors";

const GENERAL_TYPE_OPTION = { eventTypeId: null as number | null, eventTypeName: "General (todos los tipos)" };
const GENERAL_SUBTYPE_OPTION = {
  eventSubTypeId: null as number | null,
  eventSubTypeName: "General (todos los subtipos)",
};

interface ShareSettingFormValues {
  eventTypeId: number | null;
  eventSubTypeId: number | null;
  shareText: string;
}

const validationSchema = Yup.object().shape({
  shareText: Yup.string()
    .trim()
    .max(SHARE_TEXT_MAX_LENGTH, `Máximo ${SHARE_TEXT_MAX_LENGTH} caracteres`)
    .required("El texto es requerido"),
});

const formatDateTime = (value: string | null): string =>
  value ? new Date(value).toLocaleString("es-AR") : "—";

export const ShareSettingsConfig = () => {
  const tenantScope = useTenantScope();
  // Igual que en FAQs destacadas: con scope global ("Todos") no hay un tenant concreto
  // sobre el cual configurar textos de compartir, así que hay que elegir uno primero.
  const needsTenantSelection = !tenantScope.loading && !tenantScope.locked;

  const { data: settings, isLoading: isLoadingSettings } = useGetShareSettingsQuery(undefined, {
    skip: tenantScope.loading || needsTenantSelection,
  });
  const { data: eventTypes, isLoading: isLoadingEventTypes } = useGetEventTypesQuery(undefined, {
    skip: tenantScope.loading || needsTenantSelection,
  });

  const [upsertSetting, { isLoading: isSaving }] = useUpsertShareSettingMutation();
  const [deleteSetting, { isLoading: isDeleting }] = useDeleteShareSettingMutation();

  const [dialogVisible, setDialogVisible] = useState(false);
  const [settingToEdit, setSettingToEdit] = useState<TenantShareSetting | undefined>();
  const [settingToDelete, setSettingToDelete] = useState<TenantShareSetting | undefined>();

  const rows = useMemo(() => {
    const list = settings ?? [];
    return [...list].sort((a, b) =>
      formatShareSettingScopeLabel(a).localeCompare(formatShareSettingScopeLabel(b), "es")
    );
  }, [settings]);

  const typeOptions = useMemo(
    () => [GENERAL_TYPE_OPTION, ...(eventTypes ?? [])],
    [eventTypes]
  );

  const isTierLocked = !!settingToEdit;

  const formik = useFormik<ShareSettingFormValues>({
    initialValues: {
      eventTypeId: settingToEdit?.eventTypeId ?? null,
      eventSubTypeId: settingToEdit?.eventSubTypeId ?? null,
      shareText: settingToEdit?.shareText ?? "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      try {
        await upsertSetting({
          eventTypeId: values.eventTypeId,
          eventSubTypeId: values.eventSubTypeId,
          shareText: values.shareText.trim(),
        }).unwrap();
        toast.success(settingToEdit ? "Texto actualizado" : "Texto guardado");
        hideDialog();
      } catch (err: unknown) {
        toast.error(getApiErrorMessage(err) ?? "No se pudo guardar el texto para compartir");
      }
    },
  });

  const subTypeOptions = useMemo(() => {
    const selectedType = eventTypes?.find((type) => type.eventTypeId === formik.values.eventTypeId);
    return selectedType ? [GENERAL_SUBTYPE_OPTION, ...selectedType.subTypes] : [];
  }, [eventTypes, formik.values.eventTypeId]);

  const hideDialog = () => {
    setDialogVisible(false);
    setSettingToEdit(undefined);
    formik.resetForm();
  };

  const openCreateDialog = () => {
    setSettingToEdit(undefined);
    setDialogVisible(true);
  };

  const openEditDialog = (setting: TenantShareSetting) => {
    setSettingToEdit(setting);
    setDialogVisible(true);
  };

  const handleTypeChange = ({ value }: DropdownChangeEvent) => {
    formik.setFieldValue("eventTypeId", value ?? null);
    formik.setFieldValue("eventSubTypeId", null);
  };

  const handleSubTypeChange = ({ value }: DropdownChangeEvent) => {
    formik.setFieldValue("eventSubTypeId", value ?? null);
  };

  const handleDelete = async () => {
    if (!settingToDelete?.id) return;
    try {
      await deleteSetting(settingToDelete.id).unwrap();
      toast.success("Texto eliminado");
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err) ?? "No se pudo eliminar el texto");
    } finally {
      setSettingToDelete(undefined);
    }
  };

  // Al crear, avisamos si ya existe una config para ese alcance: al guardar se sobreescribe
  // (el PUT hace upsert por tier, no por id).
  const duplicateScope =
    !settingToEdit && (formik.values.eventTypeId !== null || formik.values.eventSubTypeId !== null)
      ? rows.find((row) => isSameShareSettingScope(row, formik.values))
      : undefined;

  const scopeTemplate = (row: TenantShareSetting) => (
    <div className="flex align-items-center gap-2">
      <span>{formatShareSettingScopeLabel(row)}</span>
      {row.isDefault && <Tag severity="info" value="Por defecto" />}
    </div>
  );

  const textTemplate = (row: TenantShareSetting) => {
    if (!row.shareText.trim()) return <span className="text-500">—</span>;
    return (
      <span
        className="share-setting-text-truncated block overflow-hidden text-overflow-ellipsis white-space-nowrap"
        style={{ maxWidth: "24rem" }}
        data-pr-tooltip={row.shareText}
        data-pr-position="top"
      >
        {row.shareText}
      </span>
    );
  };

  const actionsTemplate = (row: TenantShareSetting) => (
    <div className="flex gap-2">
      <Button
        type="button"
        icon="pi pi-pencil"
        rounded
        outlined
        severity="info"
        tooltip="Editar"
        tooltipOptions={{ position: "top" }}
        onClick={() => openEditDialog(row)}
      />
      {row.id !== null && (
        <Button
          type="button"
          icon="pi pi-trash"
          rounded
          outlined
          severity="danger"
          tooltip="Eliminar"
          tooltipOptions={{ position: "top" }}
          onClick={() => setSettingToDelete(row)}
        />
      )}
    </div>
  );

  return (
    <div className="p-card gap-4 p-3">
      <div className="flex w-full justify-content-center align-items-center gap-3 mt-4 mb-6">
        <h1 className="text-center m-0">Compartir</h1>
      </div>

      {needsTenantSelection ? (
        <div className="flex justify-content-center p-4">
          <Message
            severity="warn"
            text="Seleccioná un tenant en el selector superior para ver y configurar sus textos para compartir"
          />
        </div>
      ) : (
        <div className="grid">
          <div className="col-12 flex justify-content-between align-items-center mb-3">
            <small className="text-600">
              Texto que acompaña al link que arma la app al compartir un evento. Se puede
              configurar a nivel general, por tipo de evento o por sub-tipo. Se aplica el más
              específico que coincida.
            </small>
            <Button label="Agregar texto" icon="pi pi-plus" onClick={openCreateDialog} />
          </div>

          <div className="col-12">
            <Tooltip
              target=".share-setting-text-truncated"
              style={{ maxWidth: "24rem", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
            />
            <DataTable
              value={rows}
              loading={tenantScope.loading || isLoadingSettings}
              dataKey={(row: TenantShareSetting) => row.id ?? "global"}
              tableStyle={{ minWidth: "50rem" }}
              responsiveLayout="scroll"
              emptyMessage="No hay textos configurados."
            >
              <Column header="Alcance" body={scopeTemplate} style={{ width: "18rem" }} />
              <Column header="Texto" body={textTemplate} style={{ minWidth: "20rem" }} />
              <Column
                header="Actualizado"
                body={(row: TenantShareSetting) => formatDateTime(row.updatedAt)}
                style={{ width: "12rem" }}
              />
              <Column header="Acciones" body={actionsTemplate} style={{ width: "9rem" }} />
            </DataTable>
          </div>
        </div>
      )}

      <Dialog
        header={settingToEdit ? "Editar texto para compartir" : "Agregar texto para compartir"}
        visible={dialogVisible}
        style={{ width: "35vw", minWidth: "22rem" }}
        onHide={hideDialog}
        footer={
          <div>
            <Button label="Cancelar" severity="secondary" onClick={hideDialog} />
            <Button label="Guardar" loading={isSaving} onClick={() => formik.handleSubmit()} />
          </div>
        }
      >
        <div className="field">
          <label htmlFor="eventTypeId" className="font-medium text-900">
            Tipo de evento
          </label>
          <Dropdown
            id="eventTypeId"
            className="w-full mt-2"
            value={formik.values.eventTypeId}
            optionLabel="eventTypeName"
            optionValue="eventTypeId"
            options={typeOptions}
            loading={isLoadingEventTypes}
            disabled={isTierLocked}
            onChange={handleTypeChange}
          />
        </div>

        <div className="field mt-3">
          <label htmlFor="eventSubTypeId" className="font-medium text-900">
            Sub-tipo de evento
          </label>
          <Dropdown
            id="eventSubTypeId"
            className="w-full mt-2"
            value={formik.values.eventSubTypeId}
            optionLabel="eventSubTypeName"
            optionValue="eventSubTypeId"
            options={subTypeOptions}
            emptyMessage="Seleccioná un tipo primero"
            disabled={isTierLocked || formik.values.eventTypeId === null}
            onChange={handleSubTypeChange}
          />
        </div>

        {duplicateScope && (
          <Message
            className="w-full justify-content-start mt-3"
            severity="warn"
            text={`Ya existe un texto configurado para "${formatShareSettingScopeLabel(
              duplicateScope
            )}". Al guardar se va a sobreescribir.`}
          />
        )}

        <div className="field mt-3">
          <label htmlFor="shareText" className="font-medium text-900">
            Texto *
          </label>
          <InputTextarea
            id="shareText"
            className={`w-full mt-2 ${formik.touched.shareText && formik.errors.shareText ? "p-invalid" : ""
              }`}
            rows={3}
            maxLength={SHARE_TEXT_MAX_LENGTH}
            value={formik.values.shareText}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.shareText && formik.errors.shareText ? (
            <small className="p-error block mt-1">{formik.errors.shareText}</small>
          ) : (
            <small className="block mt-1 text-500">
              La app arma el link de compartir; este texto se agrega como acompañamiento.
            </small>
          )}
        </div>
      </Dialog>

      <Dialog
        header="Eliminar texto"
        visible={!!settingToDelete}
        style={{ width: "35vw" }}
        onHide={() => setSettingToDelete(undefined)}
        footer={
          <div>
            <Button
              label="Cancelar"
              severity="secondary"
              onClick={() => setSettingToDelete(undefined)}
            />
            <Button
              label="Eliminar"
              severity="danger"
              loading={isDeleting}
              onClick={handleDelete}
            />
          </div>
        }
      >
        {settingToDelete && (
          <p className="m-0">
            ¿Eliminar el texto configurado para "
            {formatShareSettingScopeLabel(settingToDelete)}"? Ese alcance vuelve a usar el
            siguiente nivel de precedencia (tipo → general).
          </p>
        )}
      </Dialog>
    </div>
  );
};
